import apiClient from "../lib/api-client";

export interface RackLocation {
  id: string;
  storeId: string;
  rackCode: string;
  rackName: string;
  zone?: string;
  row?: string;
  column?: string;
  level?: number;
  rackType?: string;
  capacity?: number;
  description?: string;
  isActive: boolean;
  store?: {
    id: string;
    name: string;
  };
  _count?: {
    batchMappings: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateRackLocationData {
  storeId: string;
  rackCode: string;
  rackName: string;
  zone?: string;
  row?: string;
  column?: string;
  level?: number;
  rackType?: string;
  capacity?: number;
  description?: string;
}

export interface RackLocationsParams {
  page?: number;
  limit?: number;
  search?: string;
  storeId?: string;
  rackType?: string;
  isActive?: boolean;
}

export interface RackLocationsResponse {
  data: RackLocation[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface InventoryLocationCommand {
  lotId: string;
  rackLocationId: string;
  quantity: string;
  expectedLocationBalanceVersion?: number;
  reason: string;
}

export interface InventoryLocationMoveCommand {
  lotId: string;
  fromRackLocationId: string;
  toRackLocationId: string;
  quantity: string;
  expectedSourceLocationBalanceVersion?: number;
  reason: string;
}

export const rackLocationService = {
  getAll: async (
    params?: RackLocationsParams,
  ): Promise<RackLocationsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.search) searchParams.set("search", params.search);
    if (params?.storeId) searchParams.set("storeId", params.storeId);
    if (params?.rackType) searchParams.set("rackType", params.rackType);
    if (params?.isActive !== undefined)
      searchParams.set("isActive", params.isActive.toString());

    const response = await apiClient.get(
      `/rack-locations?${searchParams.toString()}`,
    );
    return response.data;
  },

  getById: async (id: string): Promise<RackLocation> => {
    const response = await apiClient.get(`/rack-locations/${id}`);
    return response.data;
  },

  getByStore: async (storeId: string): Promise<RackLocation[]> => {
    const response = await apiClient.get(`/rack-locations/by-store/${storeId}`);
    return response.data;
  },

  create: async (data: CreateRackLocationData): Promise<RackLocation> => {
    const response = await apiClient.post("/rack-locations", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<CreateRackLocationData>,
  ): Promise<RackLocation> => {
    const response = await apiClient.patch(`/rack-locations/${id}`, data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/rack-locations/${id}`, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  },

  putAway: async (data: InventoryLocationCommand) => {
    const response = await apiClient.post("/product-revival/inventory/locations/put-away", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  removeFromLocation: async (data: InventoryLocationCommand) => {
    const response = await apiClient.post("/product-revival/inventory/locations/remove", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  moveInventory: async (data: InventoryLocationMoveCommand) => {
    const response = await apiClient.post("/product-revival/inventory/locations/move", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },
};
